import React, { useState, useEffect } from 'react';
import { User, Mail, Phone, MapPin, Calendar, Briefcase, Users as UsersIcon, Edit2, Save, X, Loader2 } from 'lucide-react';

const Profile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    employeeId: '',
    role: '',
    location: '',
    reportingManager: '',
    phone: '',
    dob: '',
    maritalStatus: ''
  });
  const [editData, setEditData] = useState({});

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setIsLoading(true);
      // Get current user from localStorage or session
      const currentUser = localStorage.getItem('currentUser') || 'john.doe@example.com';
      
      const response = await fetch('/api/user_management/get-users');
      const data = await response.json();
      const users = JSON.parse(data.users);
      
      // Find current user by email
      const user = users.find(u => u.Email === currentUser);
      
      if (user) {
        const profile = {
          firstName: user['First Name'] || '',
          lastName: user['Last Name'] || '',
          email: user.Email || '',
          employeeId: user['Employee ID'] || '',
          role: user.Role || '',
          location: user.Location || '',
          reportingManager: user['Reporting Manager'] || '',
          phone: user.Phone || 'Not provided',
          dob: user['Date of Birth'] || '',
          maritalStatus: user['Marital Status'] || ''
        };
        setProfileData(profile);
        setEditData(profile);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditData({ ...profileData });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditData({ ...profileData });
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      
      const params = new URLSearchParams({
        emp_id: editData.employeeId,
        first_name: editData.firstName,
        last_name: editData.lastName,
        email: editData.email,
        role: editData.role,
        reporting_manager: editData.reportingManager,
        location: editData.location,
        marital_status: editData.maritalStatus,
        dob: editData.dob
      });

      const response = await fetch(`/api/user_management/update-user?${params}`, {
        method: 'POST'
      });

      if (!response.ok) throw new Error('Failed to update profile');

      setProfileData(editData);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Loader2 style={{ width: '40px', height: '40px', color: 'var(--cares-blue)', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: '600', color: 'var(--cares-text-2)', margin: '0 0 8px 0' }}>
            My Profile
          </h1>
          <p style={{ color: 'var(--cares-text-label)', margin: 0 }}>
            View and manage your personal information
          </p>
        </div>
        
        {!isEditing ? (
          <button
            onClick={handleEdit}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              background: 'linear-gradient(135deg, var(--cares-blue) 0%, var(--cares-blue) 100%)',
              color: 'var(--cares-text-max)',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            <Edit2 style={{ width: '16px', height: '16px' }} />
            Edit Profile
          </button>
        ) : null}
      </div>

      {/* Profile Content */}
      <div style={{
        background: 'var(--cares-surface-card)',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {/* Profile Header with Avatar */}
        <div style={{
          background: 'linear-gradient(135deg, var(--cares-blue) 0%, var(--cares-blue) 100%)',
          padding: '40px 32px',
          display: 'flex',
          alignItems: 'center',
          gap: '24px'
        }}>
          <div style={{
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            background: 'var(--cares-surface-card)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '40px',
            fontWeight: '600',
            color: 'var(--cares-blue)'
          }}>
            {profileData.firstName.charAt(0)}{profileData.lastName.charAt(0)}
          </div>
          <div>
            <h2 style={{ fontSize: '32px', fontWeight: '600', color: 'var(--cares-text-max)', margin: '0 0 8px 0' }}>
              {profileData.firstName} {profileData.lastName}
            </h2>
            <p style={{ fontSize: '16px', color: 'rgba(255,255,255,0.9)', margin: 0 }}>
              {profileData.role}
            </p>
          </div>
        </div>

        {/* Profile Details */}
        <div style={{ padding: '32px' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '24px'
          }}>
            {/* Employee ID */}
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: 'var(--cares-text-label)', marginBottom: '8px' }}>
                <Briefcase style={{ width: '14px', height: '14px', display: 'inline', marginRight: '6px' }} />
                Employee ID
              </label>
              <input
                type="text"
                value={editData.employeeId}
                disabled
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid var(--cares-line-card)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: 'var(--cares-surface-board)',
                  color: 'var(--cares-text-label)',
                  cursor: 'not-allowed'
                }}
              />
            </div>

            {/* Email */}
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: 'var(--cares-text-label)', marginBottom: '8px' }}>
                <Mail style={{ width: '14px', height: '14px', display: 'inline', marginRight: '6px' }} />
                Email
              </label>
              <input
                type="email"
                value={editData.email}
                disabled
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid var(--cares-line-card)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: 'var(--cares-surface-board)',
                  color: 'var(--cares-text-label)',
                  cursor: 'not-allowed'
                }}
              />
            </div>

            {/* First Name */}
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: 'var(--cares-text-label)', marginBottom: '8px' }}>
                <User style={{ width: '14px', height: '14px', display: 'inline', marginRight: '6px' }} />
                First Name
              </label>
              <input
                type="text"
                value={editData.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                disabled={!isEditing}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${isEditing ? 'var(--cares-blue)' : 'var(--cares-line-card)'}`,
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: isEditing ? 'var(--cares-surface-card)' : 'var(--cares-surface-board)',
                  color: 'var(--cares-text-2)',
                  cursor: isEditing ? 'text' : 'not-allowed'
                }}
              />
            </div>

            {/* Last Name */}
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: 'var(--cares-text-label)', marginBottom: '8px' }}>
                <User style={{ width: '14px', height: '14px', display: 'inline', marginRight: '6px' }} />
                Last Name
              </label>
              <input
                type="text"
                value={editData.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                disabled={!isEditing}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${isEditing ? 'var(--cares-blue)' : 'var(--cares-line-card)'}`,
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: isEditing ? 'var(--cares-surface-card)' : 'var(--cares-surface-board)',
                  color: 'var(--cares-text-2)',
                  cursor: isEditing ? 'text' : 'not-allowed'
                }}
              />
            </div>

            {/* Role */}
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: 'var(--cares-text-label)', marginBottom: '8px' }}>
                <Briefcase style={{ width: '14px', height: '14px', display: 'inline', marginRight: '6px' }} />
                Role
              </label>
              <input
                type="text"
                value={editData.role}
                disabled
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid var(--cares-line-card)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: 'var(--cares-surface-board)',
                  color: 'var(--cares-text-label)',
                  cursor: 'not-allowed'
                }}
              />
            </div>

            {/* Location */}
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: 'var(--cares-text-label)', marginBottom: '8px' }}>
                <MapPin style={{ width: '14px', height: '14px', display: 'inline', marginRight: '6px' }} />
                Location
              </label>
              <input
                type="text"
                value={editData.location}
                onChange={(e) => handleChange('location', e.target.value)}
                disabled={!isEditing}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${isEditing ? 'var(--cares-blue)' : 'var(--cares-line-card)'}`,
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: isEditing ? 'var(--cares-surface-card)' : 'var(--cares-surface-board)',
                  color: 'var(--cares-text-2)',
                  cursor: isEditing ? 'text' : 'not-allowed'
                }}
              />
            </div>

            {/* Reporting Manager */}
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: 'var(--cares-text-label)', marginBottom: '8px' }}>
                <UsersIcon style={{ width: '14px', height: '14px', display: 'inline', marginRight: '6px' }} />
                Reporting Manager
              </label>
              <input
                type="text"
                value={editData.reportingManager}
                onChange={(e) => handleChange('reportingManager', e.target.value)}
                disabled={!isEditing}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${isEditing ? 'var(--cares-blue)' : 'var(--cares-line-card)'}`,
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: isEditing ? 'var(--cares-surface-card)' : 'var(--cares-surface-board)',
                  color: 'var(--cares-text-2)',
                  cursor: isEditing ? 'text' : 'not-allowed'
                }}
              />
            </div>

            {/* Date of Birth */}
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: 'var(--cares-text-label)', marginBottom: '8px' }}>
                <Calendar style={{ width: '14px', height: '14px', display: 'inline', marginRight: '6px' }} />
                Date of Birth
              </label>
              <input
                type="date"
                value={editData.dob}
                onChange={(e) => handleChange('dob', e.target.value)}
                disabled={!isEditing}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${isEditing ? 'var(--cares-blue)' : 'var(--cares-line-card)'}`,
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: isEditing ? 'var(--cares-surface-card)' : 'var(--cares-surface-board)',
                  color: 'var(--cares-text-2)',
                  cursor: isEditing ? 'text' : 'not-allowed'
                }}
              />
            </div>
          </div>

          {/* Action Buttons */}
          {isEditing && (
            <div style={{
              display: 'flex',
              gap: '12px',
              marginTop: '32px',
              paddingTop: '24px',
              borderTop: '1px solid var(--cares-line-card)'
            }}>
              <button
                onClick={handleSave}
                disabled={isSaving}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, var(--cares-green) 0%, var(--cares-green) 100%)',
                  color: 'var(--cares-text-max)',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  opacity: isSaving ? 0.7 : 1
                }}
              >
                {isSaving ? (
                  <>
                    <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save style={{ width: '16px', height: '16px' }} />
                    Save Changes
                  </>
                )}
              </button>
              <button
                onClick={handleCancel}
                disabled={isSaving}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '12px 24px',
                  background: 'var(--cares-surface-card)',
                  color: 'var(--cares-text-label)',
                  border: '1px solid var(--cares-line-card)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: isSaving ? 'not-allowed' : 'pointer'
                }}
              >
                <X style={{ width: '16px', height: '16px' }} />
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;

